import { CommonModule, formatDate, KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil, finalize, Subject, tap } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { SettingsService } from 'src/app/services/settings.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RenderFormat, IReportParameters } from '../reports-models';
import { ReportsService } from '../reports.service';
import { LocalizationService, LocalizationSettings } from 'src/app/shared/localization/localization.service';
import { ReportsUtilsService } from '../reports-utils.service';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { FormsModule } from '@angular/forms';
import { ContractsService } from 'src/app/contracts/contracts.service';

@Component({
  selector: 'app-contract-score',
  templateUrl: './contract-score.component.html',
  styleUrls: ['./contract-score.component.scss'],
  imports: [CommonModule, TranslateModule, CustomMultiSelectComponent, CustomSelectComponent, FormsModule]
})
export class ContractScoreComponent implements OnInit, OnDestroy {
  selectedPeriod = -1;
  periodOptions: KeyValue<any, string>[];
  userAssignedCustomersCount: number;
  customers$ = this.appService.customersSelectItems$
    .pipe(tap(_ => {
      this.userAssignedCustomersCount = _.length;
      if (this.userAssignedCustomersCount == 1)
        {this.selectedCustomers = [_[0].key]; }
    }));
  locale: LocalizationSettings = {} as LocalizationSettings;  
  selectedCustomers: number[];
  keyValueClaimTypes: KeyValue<string, string>[];
  renderedReport: any;
  showResults: boolean;
  claimType: string;

  private readonly unsubscribe$ = new Subject();
  private reportFolder: string;

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private localization: LocalizationService,
    private spinnerService: GlobalSpinnerService,
    private utilsService: UtilsService,
    private _sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private notificationService: MessageToUiService,
    private settingsService: SettingsService,
    private reportUtils: ReportsUtilsService,
    private contractsService: ContractsService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.periodOptions = this.reportUtils.getYearlyPeriodOptions();
    this.settingsService.getSettingValueByName('ReportServer_FolderForKreditorWeb')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(settingValue => this.reportFolder = settingValue);

    this.localization.settings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => this.locale = result);

    this.contractsService.getSelectedClaimTypesForUser()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.keyValueClaimTypes = data.map(x => ({ key: x.claimTypeCode, value: `${x.claimTypeCode} ${x.claimTypeDescription}` } as KeyValue<string, string>));
    });
  }

  onGenerateReport() {
    if (!this.isFormValid()) {
      return;
    }

    const parameters = this.getReportParameters();

    this.spinnerService.show();

    this.reportsService.renderReport(parameters)
      .pipe(finalize(() => {
        this.spinnerService.hide();
      }))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          const blobData = this.utilsService.b64toBlob(result.data, 'application/pdf');
          this.renderedReport = this._sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blobData));
          this.showResults = true;
        },
        error: () => this.notificationService.genericError()
      });
  }

  private getReportParameters(): IReportParameters {
    const parameters = {
      templatePath: `${this.reportFolder}ContractScore`,
      displayName: this.translateService.instant('Reports.ContractScore.Title'),
      format: RenderFormat.Pdf,
      parameters: {
        ClaimType: this.claimType
      }
    } as IReportParameters;

    if (this.selectedCustomers.length > 0) {
      parameters.parameters.CustomerNumbers = this.selectedCustomers?.join(',').toString();
    }

    let startDate: Date;
    let endDate: Date;

    if (this.selectedPeriod == -1) {
      endDate = new Date();
      startDate = new Date();
      startDate.setFullYear(endDate.getFullYear() - 1);
      startDate.setDate(endDate.getDate() + 1);
    } else {
      startDate = new Date(this.selectedPeriod, 0, 1);
      endDate = new Date(this.selectedPeriod, 11, 31);
    }

    parameters.parameters.StartDate = formatDate(startDate, 'yyyy-MM-dd', this.locale.culture);
    parameters.parameters.EndDate = formatDate(endDate, 'yyyy-MM-dd', this.locale.culture);

    return parameters;
  }

  private isFormValid(): boolean {
    if  (this.selectedCustomers.length == 0) {
      this.notificationService.errorKey('Reports.ContractScore.Errors.Customer');
      return false;
    } else if (!this.claimType) {
      this.notificationService.errorKey('Reports.ContractScore.Errors.ClaimType');
      return false;
    }
    
    return true;
  }
}